import { useState, useEffect } from 'react'
import { ConsultationService } from '../services/consultationService'
import { isSupabaseConfigured } from '../lib/supabase'

export const useConsultation = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [consultationData, setConsultationData] = useState(null)

  // Upload session image (called when image is uploaded in step 1)
  const uploadSessionImage = async (imageFile, channelParams = null) => {
    setLoading(true)
    setError(null)
    
    try {
      if (!isSupabaseConfigured()) {
        throw new Error('Supabase not configured properly')
      }

      const { data, error } = await ConsultationService.uploadSessionImage(imageFile, channelParams)
      
      if (error) {
        throw error
      }
      
      setConsultationData(prev => ({ 
        ...prev, 
        sessionImages: [...(prev?.sessionImages || []), data] 
      }))
      return { success: true, data }
    } catch (err) {
      setError(ConsultationService.getUploadErrorMessage(err))
      console.error('Upload session image error:', err)
      return { success: false, error: err }
    } finally {
      setLoading(false)
    }
  }

  // Save complete consultation data (called when moving from step 2 to 3)
  const saveCompleteConsultation = async (formData, loadOnly = false, channelParams = null) => {
    setLoading(true)
    setError(null)
    
    try {
      if (!isSupabaseConfigured()) {
        throw new Error('Supabase not configured properly')
      }

      const { data, error } = await ConsultationService.saveCompleteConsultation(formData, loadOnly, channelParams)
      
      if (error) {
        throw error
      }
      
      // Only update consultationData if we have valid data
      if (data && (data.user || data.consultation)) {
        setConsultationData(prev => ({ 
          ...prev, 
          user: data.user || prev?.user,
          consultation: data.consultation || prev?.consultation
        }))
      }
      
      return { success: true, data }
    } catch (err) {
      setError('خطأ في حفظ بيانات الاستشارة. يرجى التحقق من اتصالك بالإنترنت والمحاولة مرة أخرى.')
      console.error('Save complete consultation error:', err)
      return { success: false, error: err }
    } finally {
      setLoading(false)
    }
  }

  // Check for product updates
  const checkForProductUpdates = async (userId, lastUpdatedAt) => {
    setLoading(true)
    setError(null)

    try {
      if (!isSupabaseConfigured()) {
        throw new Error('Supabase not configured properly')
      }

      const result = await ConsultationService.checkForProductUpdates(userId, lastUpdatedAt)

      if (result.error) {
        throw result.error
      }

      return { success: true, ...result }
    } catch (err) {
      setError('خطأ في التحقق من تحديثات المنتجات')
      console.error('Check product updates error:', err)
      return { success: false, error: err }
    } finally {
      setLoading(false)
    }
  }

  // Get fresh consultation data
  const getFreshConsultationData = async (userId) => {
    setLoading(true)
    setError(null)

    try {
      if (!isSupabaseConfigured()) {
        throw new Error('Supabase not configured properly')
      }

      const { data, error } = await ConsultationService.getFreshConsultationData(userId)

      if (error) {
        throw error
      }

      // Only update consultationData if we have valid data
      if (data && (data.user || data.consultation || data.images)) {
        setConsultationData(prev => ({
          ...prev,
          user: data.user || prev?.user,
          consultation: data.consultation || prev?.consultation,
          images: data.images || prev?.images
        }))
      }

      return { success: true, data }
    } catch (err) {
      setError('خطأ في تحديث بيانات الاستشارة')
      console.error('Get fresh consultation data error:', err)
      return { success: false, error: err }
    } finally {
      setLoading(false)
    }
  }

  // Load existing consultation data on component mount - DISABLED FOR NOW
  useEffect(() => {
    // Comment out the data loading to prevent 406 errors for new users
    // We'll only load data when user actually has data to load
    console.log('Initial data loading disabled - will load data as user interacts')
  }, [])

  return {
    loading,
    error,
    consultationData,
    uploadSessionImage,
    saveCompleteConsultation,
    checkForProductUpdates,
    getFreshConsultationData,
    clearError: () => setError(null),
    setErrorMessage: (message) => setError(message)
  }
}
